import { PrismicRichText, SliceZone } from '@prismicio/react';

import { useIntl } from 'react-intl';

import { paths } from '@temp/paths';

import { ImageMarker } from '@components/molecules/ImageMarker';
import { VideoPlayer } from '@components/molecules/VideoPlayer';
import { PostsSlider } from '@components/organisms/PostsSlider';

import { ISlice } from '@types';

import { IProject, IProjects } from './types';
import * as S from './styles';
import { components } from './Slices';

export interface PageProps {
  project: IProject;
  projects: IProjects[];
  slices: ISlice[];
}

const Page: React.FC<PageProps> = ({ project, projects, slices }) => {
  const intl = useIntl();
  return (
    <S.Wrapper className="realisation-project">
      <S.Container>
        <S.Title>{project.title}</S.Title>
        {project && (
          <S.Information>
            <S.Images>
              {project.media.map((z, i) =>
                z?.video ? (
                  <VideoPlayer {...z} key={`project-video-${i}`} />
                ) : (
                  <ImageMarker {...z} key={`project-image-marker-${i}`} />
                )
              )}
            </S.Images>
            <S.Content>
              <S.Description>
                <PrismicRichText field={project.description} />
              </S.Description>
              <S.Attributes>
                {project.category && (
                  <S.AttributeItem>
                    <S.AttributeName>
                      {intl.formatMessage({
                        defaultMessage: 'Style:',
                        id: 'Nul8w0',
                      })}
                    </S.AttributeName>
                    <S.AttributeValue>{project.category}</S.AttributeValue>
                  </S.AttributeItem>
                )}
                {project.door_size && (
                  <S.AttributeItem>
                    <S.AttributeName>
                      {intl.formatMessage({
                        defaultMessage: 'Door size:',
                        id: 'yKVXwz',
                      })}
                    </S.AttributeName>
                    <S.AttributeValue>{project.door_size}</S.AttributeValue>
                  </S.AttributeItem>
                )}
                {project.color && (
                  <S.AttributeItem>
                    <S.AttributeName>
                      {intl.formatMessage({
                        defaultMessage: 'Door color:',
                        id: 'YC1Aw1',
                      })}
                    </S.AttributeName>
                    <S.AttributeValue>{project.color}</S.AttributeValue>
                  </S.AttributeItem>
                )}
                {project.glass_type &&
                  (project.category || '').includes('Industrial') && (
                    <S.AttributeItem>
                      <S.AttributeName>
                        {intl.formatMessage({
                          defaultMessage: 'Glass type:',
                          id: '/8eufO',
                        })}
                      </S.AttributeName>
                      <S.AttributeValue>{project.glass_type}</S.AttributeValue>
                    </S.AttributeItem>
                  )}
                {project.handle_style && (
                  <S.AttributeItem>
                    <S.AttributeName>
                      {intl.formatMessage({
                        defaultMessage: 'Handle:',
                        id: '5QCYcD',
                      })}
                    </S.AttributeName>
                    <S.AttributeValue>{project.handle_style}</S.AttributeValue>
                  </S.AttributeItem>
                )}
                {project.handle_color && (
                  <S.AttributeItem>
                    <S.AttributeName>
                      {intl.formatMessage({
                        defaultMessage: 'Handle color:',
                        id: 'HfNop+',
                      })}
                    </S.AttributeName>
                    <S.AttributeValue>{project.handle_color}</S.AttributeValue>
                  </S.AttributeItem>
                )}
                {project.interior_designer_link && (
                  <S.AttributeItem>
                    <S.AttributeName>
                      {intl.formatMessage({
                        defaultMessage: 'Project:',
                        id: '402guk',
                      })}
                    </S.AttributeName>
                    <S.AttributeValueLink
                      as="a"
                      href={project.interior_designer_link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {project.interior_designer_view_name ||
                        project.interior_designer_link}
                    </S.AttributeValueLink>
                  </S.AttributeItem>
                )}
                {project.product_link && (
                  <S.AttributeItem>
                    <S.AttributeName>
                      {intl.formatMessage({
                        defaultMessage: 'Product:',
                        id: 'jgPXwd',
                      })}
                    </S.AttributeName>
                    <S.AttributeValueLink as="a" href={project.product_link}>
                      {project.product_view_name || project.product_link}
                    </S.AttributeValueLink>
                  </S.AttributeItem>
                )}
                {/* {project.width && (
                  <S.AttributeItem>
                    <S.AttributeName>
                      {intl.formatMessage({
                        defaultMessage: "Width:",
                      })}
                    </S.AttributeName>
                    <S.AttributeValue>{project.width} cm</S.AttributeValue>
                  </S.AttributeItem>
                )}
                {project.height && (
                  <S.AttributeItem>
                    <S.AttributeName>
                      {intl.formatMessage({
                        defaultMessage: "Height:",
                      })}
                    </S.AttributeName>
                    <S.AttributeValue>{project.height} cm</S.AttributeValue>
                  </S.AttributeItem>
                )} */}
              </S.Attributes>
              {Boolean(project.tag_list?.length) && (
                <S.Tags>
                  {project.tag_list.map((z, i) => (
                    <S.TagItem key={`project_tag_${i}`}>{z.name}</S.TagItem>
                  ))}
                </S.Tags>
              )}
            </S.Content>
          </S.Information>
        )}
      </S.Container>
      <S.Slices>
        <SliceZone slices={slices} components={components} />
      </S.Slices>
      <S.Other>
        <PostsSlider path={paths.inspirations} posts={projects} />
      </S.Other>
    </S.Wrapper>
  );
};

Page.displayName = 'ProjectPage';

export default Page;
